import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Navigation } from '@haltongroup/halton-ui-library';
import type { IIconName } from '@haltongroup/halton-ui-library';
import './i18n'; // Initialize i18n
import './styles/index.css';
import LandingPage from './components/LandingPage';
import Calculator from './components/Calculator';
import WorkOrderForm from './components/WorkOrderForm';
import Archive from './components/Archive';
import Settings from './components/Settings';
import PrintView from './components/PrintView';
import WorkOrderPrintView from './components/WorkOrderPrintView';
import MaintenanceReportForm from './components/MaintenanceReportForm';
import MaintenanceReportPrintView from './components/MaintenanceReportPrintView';
import AnsulCommissioningForm from './components/AnsulCommissioningForm';
import AnsulCommissioningPrintView from './components/AnsulCommissioningPrintView';
import { GoogleMapsProvider } from './components/GoogleMapsProvider';

import {
  PageType,
  TravelItem,
  WorkItem,
  PartItem,
  CustomLineItem,
  SavedOffer,
  SavedWorkOrder,
  MaintenanceReport,
  SavedAnsulCommissioning,
  CalculatedTotals,
  UILocale,
  QuoteSections
} from './types';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import {
  loadOffers,
  loadWorkOrders,
  saveWorkOrders,
  loadMaintenanceReports,
  loadAnsulCommissionings,
  calculateTotals,
  createWorkOrderFromOffer,
  updateOffer
} from './utils';

// Session storage key for calculator form persistence
const CALCULATOR_SESSION_KEY = 'halton_calculator_form_session';

function AppContent() {
  const { t, i18n } = useTranslation();
  const { settings, updateSettings } = useSettings();

  // Navigation State
  const [activePage, setActivePage] = useState<PageType>('landing');

  // Calculator State - load from sessionStorage if available
  const getInitialCalculatorState = () => {
    const saved = sessionStorage.getItem(CALCULATOR_SESSION_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved calculator form:', e);
      }
    }
    return {
      customerName: '',
      customerAddress: '',
      customerNotes: '',
      addressType: 'both' as const,
      jobNumber: '',
      internalAccountId: '',
      siteLocationId: '',
      vatId: '',
      travelItems: [],
      workItems: [],
      parts: [],
      customLines: [],
      sections: {},
    };
  };

  const initialCalcState = getInitialCalculatorState();
  const [customerName, setCustomerName] = useState(initialCalcState.customerName);
  const [customerAddress, setCustomerAddress] = useState(initialCalcState.customerAddress);
  const [customerNotes, setCustomerNotes] = useState(initialCalcState.customerNotes);
  const [addressType, setAddressType] = useState<'billing' | 'site' | 'both'>(initialCalcState.addressType || 'both');
  const [jobNumber, setJobNumber] = useState(initialCalcState.jobNumber || '');
  // Master reference stamps (F2): IA#####/SL-##### selected from the snapshot
  const [offerInternalAccountId, setOfferInternalAccountId] = useState<string>(initialCalcState.internalAccountId || '');
  const [offerSiteLocationId, setOfferSiteLocationId] = useState<string>(initialCalcState.siteLocationId || '');
  const [offerVatId, setOfferVatId] = useState<string>(initialCalcState.vatId || '');
  const [travelItems, setTravelItems] = useState<TravelItem[]>(initialCalcState.travelItems);
  const [workItems, setWorkItems] = useState<WorkItem[]>(initialCalcState.workItems);
  const [parts, setParts] = useState<PartItem[]>(initialCalcState.parts);
  const [customLines, setCustomLines] = useState<CustomLineItem[]>(initialCalcState.customLines);
  const [offerSections, setOfferSections] = useState<QuoteSections>(initialCalcState.sections || {});

  // Archive State
  const [savedOffers, setSavedOffers] = useState<SavedOffer[]>([]);
  const [savedWorkOrders, setSavedWorkOrders] = useState<SavedWorkOrder[]>([]);
  const [maintenanceReports, setMaintenanceReports] = useState<MaintenanceReport[]>([]);
  const [ansulCommissionings, setAnsulCommissionings] = useState<SavedAnsulCommissioning[]>([]);

  // Edit Mode
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [editingWorkOrderId, setEditingWorkOrderId] = useState<string | null>(null);
  const [editingMaintenanceReport, setEditingMaintenanceReport] = useState<MaintenanceReport | null>(null);
  const [editingAnsulCommissioning, setEditingAnsulCommissioning] = useState<SavedAnsulCommissioning | null>(null);

  // Work Order Initial Data (from offer)
  const [workOrderInitialData, setWorkOrderInitialData] = useState<any>(null);
  const [workOrderPricingItems, setWorkOrderPricingItems] = useState<{
    travelItems: TravelItem[];
    workItems: WorkItem[];
    parts: PartItem[];
    customLines: CustomLineItem[];
  } | null>(null);

  // Print State
  const [showPrintView, setShowPrintView] = useState(false);
  const [printData, setPrintData] = useState<SavedOffer | null>(null); // Offer to print
  const [workOrderToPrint, setWorkOrderToPrint] = useState<SavedWorkOrder | null>(null);
  const [workOrderPrintSource, setWorkOrderPrintSource] = useState<'form' | 'archive'>('archive');
  const [maintenanceReportToPrint, setMaintenanceReportToPrint] = useState<MaintenanceReport | null>(null);
  const [ansulCommissioningToPrint, setAnsulCommissioningToPrint] = useState<SavedAnsulCommissioning | null>(null);

  // Load settings and data on mount
  useEffect(() => {
    setSavedOffers(loadOffers());
    setSavedWorkOrders(loadWorkOrders());
    setMaintenanceReports(loadMaintenanceReports());
    setAnsulCommissionings(loadAnsulCommissionings());
  }, []);

  // Save calculator form to sessionStorage whenever it changes
  useEffect(() => {
    const calcState = {
      customerName,
      customerAddress,
      customerNotes,
      addressType,
      jobNumber,
      internalAccountId: offerInternalAccountId,
      siteLocationId: offerSiteLocationId,
      vatId: offerVatId,
      travelItems,
      workItems,
      parts,
      customLines,
      sections: offerSections,
    };
    sessionStorage.setItem(CALCULATOR_SESSION_KEY, JSON.stringify(calcState));
  }, [customerName, customerAddress, customerNotes, addressType, jobNumber, offerInternalAccountId, offerSiteLocationId, offerVatId, travelItems, workItems, parts, customLines, offerSections]);

  // Calculate totals
  const calculatedTotals: CalculatedTotals = useMemo(() => {
    return calculateTotals(travelItems, workItems, parts, customLines, settings);
  }, [travelItems, workItems, parts, customLines, settings]);

  // Handlers
  const handleLanguageChange = (newLanguage: UILocale) => {
    updateSettings({ ...settings, uiLanguage: newLanguage });
    i18n.changeLanguage(newLanguage);
  };

  const handlePrintMaintenanceReport = (report: MaintenanceReport) => {
    setMaintenanceReportToPrint(report);
    setShowPrintView(true);
  };

  const handleEditMaintenanceReport = (report: MaintenanceReport) => {
    setEditingMaintenanceReport(report);
    setActivePage('maintenance');
  };

  const handleEditAnsulCommissioning = (report: SavedAnsulCommissioning) => {
    setEditingAnsulCommissioning(report);
    setActivePage('ansul-commissioning');
  };

  const handlePrintAnsulCommissioning = (report: SavedAnsulCommissioning) => {
    setAnsulCommissioningToPrint(report);
    setShowPrintView(true);
  };

  const handleClosePrint = () => {
    setShowPrintView(false);
    setWorkOrderToPrint(null);
    setPrintData(null);
    setMaintenanceReportToPrint(null);
    setAnsulCommissioningToPrint(null);
  };

  const handleNavigate = (page: PageType) => {
    setActivePage(page);
    setShowPrintView(false);
    // Clear work order initial data when navigating away
    if (page !== 'workorder') {
      setWorkOrderInitialData(null);
      setWorkOrderPricingItems(null);
    }
    // Clear maintenance report editing when navigating away
    if (page !== 'maintenance') {
      setEditingMaintenanceReport(null);
    }
    // Clear ansul commissioning editing when navigating away
    if (page !== 'ansul-commissioning') {
      setEditingAnsulCommissioning(null);
    }
  };

  const handleClearCalculator = () => {
    // Clear sessionStorage when explicitly clearing the form
    sessionStorage.removeItem(CALCULATOR_SESSION_KEY);
    setCustomerName('');
    setCustomerAddress('');
    setCustomerNotes('');
    setAddressType('both');
    setJobNumber('');
    setOfferInternalAccountId('');
    setOfferSiteLocationId('');
    setOfferVatId('');
    setTravelItems([]);
    setWorkItems([]);
    setParts([]);
    setCustomLines([]);
    setOfferSections({});
    setEditingOfferId(null);
  };

  const handleLoadOffer = (offer: SavedOffer) => {
    setCustomerName(offer.customerName);
    setCustomerAddress(offer.customerAddress);
    setCustomerNotes(offer.customerNotes);
    setAddressType(offer.addressType || 'both');
    setJobNumber(offer.jobNumber || '');
    setOfferInternalAccountId(offer.internalAccountId || '');
    setOfferSiteLocationId(offer.siteLocationId || '');
    setOfferVatId(offer.vatId || '');
    setTravelItems(offer.travelItems);
    setWorkItems(offer.workItems);
    setParts(offer.parts);
    setCustomLines(offer.customLines);
    setOfferSections(offer.sections || {});
    setEditingOfferId(offer.id);
    setActivePage('calculator');
  };

  const handleCopyOffer = (offer: SavedOffer) => {
    // Copy all data but don't set editingOfferId - this creates a new offer
    setCustomerName(offer.customerName);
    setCustomerAddress(offer.customerAddress);
    setCustomerNotes(offer.customerNotes);
    setAddressType(offer.addressType || 'both');
    setOfferInternalAccountId(offer.internalAccountId || '');
    setOfferSiteLocationId(offer.siteLocationId || '');
    setOfferVatId(offer.vatId || '');
    setTravelItems([...offer.travelItems]);
    setWorkItems([...offer.workItems]);
    setParts([...offer.parts]);
    setCustomLines([...offer.customLines]);
    setOfferSections(offer.sections ? JSON.parse(JSON.stringify(offer.sections)) : {});
    setEditingOfferId(null); // Ensures a NEW offer will be created
    setActivePage('calculator');
  };

  const handleCopyWorkOrder = (wo: SavedWorkOrder) => {
    // Copy form data but clear id-specific fields
    const copiedFormData = {
      ...wo.formData,
      // Clear fields that should be unique per work order
      tyonAlkuaika: '',
      tyonLoppuaika: '',
    };
    setWorkOrderInitialData(copiedFormData);
    
    // Copy pricing items
    setWorkOrderPricingItems({
      travelItems: wo.travelItems ? [...wo.travelItems] : [],
      workItems: wo.workItems ? [...wo.workItems] : [],
      parts: wo.parts ? [...wo.parts] : [],
      customLines: wo.customLines ? [...wo.customLines] : [],
    });
    
    setEditingWorkOrderId(null); // Ensures a NEW work order will be created
    setActivePage('workorder');
  };

  const handlePrintOffer = (offer: SavedOffer) => {
    setPrintData(offer);
    setShowPrintView(true);
  };

  const handlePrintWorkOrder = (workOrder: SavedWorkOrder, source: 'form' | 'archive' = 'archive') => {
    setWorkOrderToPrint(workOrder);
    setWorkOrderPrintSource(source);
    setShowPrintView(true);
  };

  const refreshArchive = () => {
    setSavedOffers(loadOffers());
    setSavedWorkOrders(loadWorkOrders());
    setMaintenanceReports(loadMaintenanceReports());
    setAnsulCommissionings(loadAnsulCommissionings());
  };

  const handleCalculatorSaved = (savedOfferId?: string) => {
    refreshArchive();
    if (savedOfferId) {
      setEditingOfferId(savedOfferId);
    }
  };

  const handleCreateWorkOrderFromOffer = (offer: SavedOffer) => {
    // Use the helper to map offer data to work order form
    const mappedFormData = createWorkOrderFromOffer(offer);

    // Set initial form data for WorkOrderForm
    setWorkOrderInitialData(mappedFormData);

    // Set pricing items from offer
    setWorkOrderPricingItems({
      travelItems: offer.travelItems,
      workItems: offer.workItems,
      parts: offer.parts,
      customLines: offer.customLines,
    });

    // Navigate to work order page
    setActivePage('workorder');

    // Show success message with offer reference
    alert(t('workOrder.createdFromOffer', { documentNumber: offer.documentNumber }));
  };

  // Print Views
  if (showPrintView) {
    return (
      <>
        {printData && (
          <PrintView
            offer={printData}
            settings={settings}
            onClose={handleClosePrint}
            onUpdateOffer={(updates) => {
              const updated = { ...printData, ...updates, updatedAt: new Date().toISOString() };
              updateOffer(updated);
              setPrintData(updated);
              setSavedOffers(loadOffers());
            }}
          />
        )}

        {workOrderToPrint && (
          <WorkOrderPrintView
            workOrder={workOrderToPrint}
            settings={settings}
            onClose={handleClosePrint}
            hasUnsavedChanges={workOrderPrintSource === 'form'}
            onSave={workOrderPrintSource === 'form' ? () => {
              // Save the work order and return the saved version
              const existingWorkOrders = loadWorkOrders();
              const existingIndex = existingWorkOrders.findIndex(wo => wo.id === workOrderToPrint.id);
              
              if (existingIndex >= 0) {
                // Update existing
                existingWorkOrders[existingIndex] = {
                  ...workOrderToPrint,
                  updatedAt: new Date().toISOString()
                };
              } else {
                // Add new
                existingWorkOrders.push(workOrderToPrint);
              }
              
              saveWorkOrders(existingWorkOrders);
              refreshArchive();
              setWorkOrderPrintSource('archive'); // Mark as saved now
              return workOrderToPrint;
            } : undefined}
          />
        )}

        {maintenanceReportToPrint && (
          <MaintenanceReportPrintView
            report={maintenanceReportToPrint}
            onClose={handleClosePrint}
          />
        )}

        {ansulCommissioningToPrint && (
          <AnsulCommissioningPrintView
            report={ansulCommissioningToPrint}
            onClose={handleClosePrint}
          />
        )}
      </>
    );
  }

  const sections = [
    {
      items: [
        { key: 'landing', label: t('nav.home'), icon: 'home' as IIconName, active: activePage === 'landing' },
        { key: 'calculator', label: t('nav.calculator'), icon: 'table' as IIconName, active: activePage === 'calculator' },
        { key: 'workorder', label: t('nav.workOrder'), icon: 'wrench' as IIconName, active: activePage === 'workorder' },
      ],
    },
    {
      title: t('nav.serviceReports'),
      separator: true,
      items: [
        { key: 'maintenance', label: t('nav.maintenance'), icon: 'file-text' as IIconName, active: activePage === 'maintenance' },
        { key: 'ansul-commissioning', label: t('ansulCommissioning.nav.title'), icon: 'flame' as IIconName, active: activePage === 'ansul-commissioning' },
      ],
    },
    {
      separator: true,
      items: [
        { key: 'archive', label: t('nav.archive'), icon: 'layers' as IIconName, active: activePage === 'archive' },
      ],
    },
  ];

  const footerItems = [
    {
      key: 'language-switcher',
      label: t('nav.language'),
      icon: 'languages' as IIconName,
      content: (
        <div className="sidebar-language-selector">
          <button
            className={`sidebar-lang-btn ${settings.uiLanguage === 'fi' ? 'active' : ''}`}
            onClick={() => handleLanguageChange('fi')}
            aria-label="Switch to Finnish"
          >
            FI
          </button>
          <button
            className={`sidebar-lang-btn ${settings.uiLanguage === 'en' ? 'active' : ''}`}
            onClick={() => handleLanguageChange('en')}
            aria-label="Switch to English"
          >
            EN
          </button>
        </div>
      ),
    },
    {
      key: 'settings',
      label: t('nav.settings'),
      icon: 'settings' as IIconName,
      active: activePage === 'settings',
    },
  ];

  const handleNavItemClick = (key: string) => {
    handleNavigate(key as PageType);
  };

  return (
    <Navigation
      sections={sections}
      footerItems={footerItems}
      desktopLogo={<img src="/halton-logo.png" alt="Halton" style={{ height: '28px', width: 'auto' }} />}
      collapsedLogo={<img src="/halton-logo.png" alt="Halton" style={{ height: '20px', width: 'auto' }} />}
      mobileLogo={<img src="/halton-logo.png" alt="Halton" style={{ height: '24px', width: 'auto' }} />}
      brandName="Service Laama"
      version="V2.0"
      onItemClick={handleNavItemClick}
      className="halton-service-nav"
    >
      <div className="app-content-area">

      <main className="main-content">
        {activePage === 'landing' && (
          <LandingPage
            onNavigate={handleNavigate}
            savedOffersCount={savedOffers.length}
            savedWorkOrdersCount={savedWorkOrders.length}
          />
        )}

        {activePage === 'calculator' && (
          <Calculator
            settings={settings}
            customerName={customerName}
            setCustomerName={setCustomerName}
            customerAddress={customerAddress}
            setCustomerAddress={setCustomerAddress}
            customerNotes={customerNotes}
            setCustomerNotes={setCustomerNotes}
            addressType={addressType}
            setAddressType={setAddressType}
            travelItems={travelItems}
            setTravelItems={setTravelItems}
            workItems={workItems}
            setWorkItems={setWorkItems}
            parts={parts}
            setParts={setParts}
            customLines={customLines}
            setCustomLines={setCustomLines}
            calculatedTotals={calculatedTotals}
            editingOfferId={editingOfferId}
            jobNumber={jobNumber}
            setJobNumber={setJobNumber}
            internalAccountId={offerInternalAccountId}
            setInternalAccountId={setOfferInternalAccountId}
            siteLocationId={offerSiteLocationId}
            setSiteLocationId={setOfferSiteLocationId}
            vatId={offerVatId}
            setVatId={setOfferVatId}
            sections={offerSections}
            setSections={setOfferSections}
            onClear={handleClearCalculator}
            onSaved={handleCalculatorSaved}
            onPrint={handlePrintOffer}
            onCreateWorkOrder={handleCreateWorkOrderFromOffer}
          />
        )}

        {activePage === 'maintenance' && (
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">
                {t('maintenance.title')}
                <Chip label="BETA" theme="warning" priorityHigh className="page-chip" />
              </h1>
              <p className="text-gray-600 mt-2">
                {t('maintenance.selectTemplate')}
              </p>
            </div>
            <MaintenanceReportForm
              onSave={() => {
                refreshArchive();
                setEditingMaintenanceReport(null);
              }}
              onPreview={handlePrintMaintenanceReport}
              editReport={editingMaintenanceReport}
            />
          </div>
        )}

        {activePage === 'ansul-commissioning' && (
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">
                {t('ansulCommissioning.title')}
              </h1>
              <p className="text-gray-600 mt-2">
                {t('ansulCommissioning.subtitle')}
              </p>
            </div>
            <AnsulCommissioningForm
              onSave={() => {
                setEditingAnsulCommissioning(null);
              }}
              onPreview={handlePrintAnsulCommissioning}
              editReport={editingAnsulCommissioning}
            />
          </div>
        )}

        {/* Work Order Form */}
        {activePage === 'workorder' && (
          <WorkOrderForm
            settings={settings}
            editingOfferId={editingOfferId}
            editingWorkOrderId={editingWorkOrderId}
            setEditingWorkOrderId={setEditingWorkOrderId}
            onSaved={refreshArchive}
            onNavigateToCalculator={() => setActivePage('calculator')}
            onPrint={handlePrintWorkOrder}
            onCreateWorkOrderFromOffer={handleCreateWorkOrderFromOffer}
            initialFormData={workOrderInitialData}
            pricingItems={workOrderPricingItems || undefined}
            onPricingItemsChange={(items) => setWorkOrderPricingItems(items)}
          />
        )}

        {activePage === 'archive' && (
          <Archive
            savedOffers={savedOffers}
            savedWorkOrders={savedWorkOrders}
            maintenanceReports={maintenanceReports}
            ansulCommissionings={ansulCommissionings}
            onEditOffer={handleLoadOffer}
            onCopyOffer={handleCopyOffer}
            onCopyWorkOrder={handleCopyWorkOrder}
            onPrintOffer={handlePrintOffer}
            onPrintWorkOrder={handlePrintWorkOrder}
            onPrintMaintenanceReport={handlePrintMaintenanceReport}
            onEditMaintenanceReport={handleEditMaintenanceReport}
            onEditAnsulCommissioning={handleEditAnsulCommissioning}
            onPrintAnsulCommissioning={handlePrintAnsulCommissioning}
            onRefresh={refreshArchive}
            onEditWorkOrder={(wo) => {
              setEditingWorkOrderId(wo.id);
              // Load pricing items from saved work order
              setWorkOrderPricingItems({
                travelItems: wo.travelItems || [],
                workItems: wo.workItems || [],
                parts: wo.parts || [],
                customLines: wo.customLines || [],
              });
              setActivePage('workorder');
            }}
            onCreateWorkOrderFromOffer={handleCreateWorkOrderFromOffer}
          />
        )}

        {activePage === 'settings' && (
          <Settings
            settings={settings}
            onUpdateSettings={updateSettings}
            onResetData={refreshArchive}
          />
        )}
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <span>© {new Date().getFullYear()} Halton Group</span>
        </div>
      </footer>

      </div>
    </Navigation>
  );
}

function App() {
  return (
    <SettingsProvider>
      <GoogleMapsProvider>
        <AppContent />
      </GoogleMapsProvider>
    </SettingsProvider>
  );
}

export default App;
